// @ts-check
import { ArrowDown, ArrowUp, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { adminProductRelatedService } from '../services/adminProductExtrasService';
import adminProductService from '../services/adminProductService';

const ProductRelatedManager = ({ productId }) => {
  const toast = useToast();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);

  const loadPicks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminProductRelatedService.getRelatedPicks(productId);
      setPicks(data);
    } catch (err) {
      toast.error('Failed to load related products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId, toast]);

  useEffect(() => {
    loadPicks();
  }, [loadPicks]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Debounced search — excludes this product itself and anything already picked. Runs as
  // soon as the panel opens (browsing the default top products) and re-filters on every
  // keystroke, rather than showing nothing until the admin types something.
  useEffect(() => {
    if (!showSearch) return;

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const query = searchQuery.trim();
        const data = await adminProductService.getAllProducts(query ? { search: query, limit: 10 } : { limit: 10 });
        const items = data.items || data;
        const excludeIds = new Set([productId, ...picks.map((p) => p.id)]);
        setSearchResults(Array.isArray(items) ? items.filter((p) => !excludeIds.has(String(p.id))) : []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, showSearch, picks, productId]);

  const handleAdd = async (product) => {
    try {
      await adminProductRelatedService.addRelatedPick(productId, product.id);
      toast.success(`Added "${product.name}" to related products`);
      // Keep the query and results as-is — refreshing `picks` re-runs the search effect
      // above and it naturally drops the just-added product via excludeIds, instead of
      // wiping the whole result list and forcing the admin to retype to add another.
      await loadPicks();
    } catch (err) {
      toast.error(err.message || 'Failed to add product');
      console.error(err);
    }
  };

  const handleRemove = async (relatedProductId) => {
    const pick = picks.find((p) => p.id === relatedProductId);
    if (!pick) return;

    const prev = [...picks];
    setPicks(picks.filter((p) => p.id !== relatedProductId));

    try {
      await adminProductRelatedService.deleteRelatedPick(productId, relatedProductId);
      toast.success(`Removed "${pick.name}" from related products`);
    } catch (err) {
      toast.error('Failed to remove product');
      console.error(err);
      setPicks(prev);
    }
  };

  const handleReorder = async (relatedProductId, direction) => {
    const idx = picks.findIndex((p) => p.id === relatedProductId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= picks.length) return;

    const updated = [...picks];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setPicks(updated);

    try {
      await adminProductRelatedService.reorderRelatedPicks(
        productId,
        updated.map((p) => p.id),
      );
    } catch (err) {
      toast.error('Failed to reorder products');
      console.error(err);
      loadPicks();
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading related products...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground/70">
          Shown as "Complete the Look" on the product page — other items pictured in the same shoot that customers can
          also buy. Only shows on the site when at least one product is added here.
        </p>
        <button
          type="button"
          onClick={() => {
            setShowSearch(!showSearch);
            setSearchQuery('');
            setSearchResults([]);
          }}
          className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-primary/90 transition flex-shrink-0"
        >
          <Plus size={14} />
          Add Product
        </button>
      </div>

      {showSearch && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name..."
              className="w-full pl-9 pr-9 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-foreground"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {searching && <p className="text-xs text-muted-foreground/70">Searching...</p>}

          {searchResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {searchResults.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  onClick={() => handleAdd(product)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition text-left"
                >
                  <div className="flex-shrink-0 w-10 h-13 bg-muted rounded-lg overflow-hidden">
                    {product.image_urls?.[0] ? (
                      <img src={product.image_urls[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-[9px]">
                        No img
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      ₹{product.price}
                      {product.status !== 'published' && (
                        <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">{product.status}</span>
                      )}
                    </p>
                  </div>
                  <Plus size={16} className="flex-shrink-0 text-muted-foreground/70" />
                </button>
              ))}
            </div>
          )}

          {!searching && searchResults.length === 0 && (
            <p className="text-xs text-muted-foreground/70 text-center py-2">
              {searchQuery.trim() ? 'No matching products found.' : 'No products available to add.'}
            </p>
          )}
        </div>
      )}

      {picks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No "Complete the Look" picks yet. Click "Add Product" to pick some.
        </div>
      ) : (
        <div className="space-y-2">
          {picks.map((pick, index) => (
            <div key={pick.id} className="flex items-center gap-4 p-3 border border-border rounded-lg bg-card">
              <div className="flex-shrink-0 w-12 h-16 bg-muted rounded-lg overflow-hidden">
                {pick.image_url ? (
                  <img src={pick.image_url} alt={pick.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-xs">
                    No img
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{pick.name}</p>
                <p className="text-xs text-muted-foreground">
                  ₹{pick.price}
                  {pick.status !== 'published' && (
                    <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">
                      {pick.status} — hidden from customers
                    </span>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => handleReorder(pick.id, 'up')}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReorder(pick.id, 'down')}
                    disabled={index === picks.length - 1}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(pick.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Remove from related products"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductRelatedManager;
